/**
 * Service Ops Detail Modal — rehab (May 2026).
 *
 * Replaces the old read-only modal (which reused ExpandedDetail from
 * ServiceLeads) with a tabbed layout inspired by the Inspection Editor:
 *
 *  ┌─ Header strip ────────────────────────────────────────────────┐
 *  │  car · customer · slot · status · % complete · refresh · auto │
 *  ├──────────────┬────────────────────────────────────────────────┤
 *  │  Overview     │                                                │
 *  │  Mechanic     │            <Tab Content>                       │
 *  │  Spare Parts  │                                                │
 *  │  Payments     │                                                │
 *  │  Invoice      │                                                │
 *  │  Activity     │                                                │
 *  └──────────────┴────────────────────────────────────────────────┘
 */

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  RefreshCw, X, Car, User, Package, FileText, Activity, Save,
  AlertTriangle, Camera, Wrench,
} from "lucide-react";

import { serviceOpsApi, sparePartsMasterApi } from "../../services/api";
import { formatDate, formatTime, formatDateTime } from "../../utils/datetime";
import OverviewTab from "./tabs/OverviewTab";
import PricingSparesTab from "./tabs/PricingSparesTab";
import ServiceMediaTab from "./tabs/ServiceMediaTab";
import ServiceObdTab from "./tabs/ServiceObdTab";
import InvoiceTab from "./tabs/InvoiceTab";
import ActivityTab from "./tabs/ActivityTab";
import { useStatusCatalogue, requiresReason } from "./useStatusCatalogue";

// Mirrors backend REJECTION_REASONS in routes/service_jobs.py. Kept in
// sync manually — if you add a new reason there, add the human label
// here so the amber banner doesn't fall back to the raw enum code.
const REJECTION_LABELS = {
  CUSTOMER_NOT_REACHABLE: "Customer not reachable",
  WRONG_LOCATION: "Wrong location / address",
  VEHICLE_NOT_ACCESSIBLE: "Vehicle not accessible",
  VEHICLE_UNFIT_FOR_SERVICE: "Vehicle unfit for service",
  OUT_OF_CITY: "Out of city",
  PERSONAL_EMERGENCY: "Personal emergency",
  DUPLICATE_BOOKING: "Duplicate booking",
  WRONG_PACKAGE_BOOKED: "Wrong package booked",
  OTHER: "Other reason",
};

const humaniseRejectionCode = (code) =>
  REJECTION_LABELS[(code || "").toUpperCase()] || code || "Rejected";

const TABS = [
  { id: "overview", label: "Overview", icon: User, Component: OverviewTab },
  { id: "pricing", label: "Pricing & Spares", icon: Package, Component: PricingSparesTab },
  { id: "media", label: "Service Media", icon: Camera, Component: ServiceMediaTab },
  { id: "obd", label: "OBD2", icon: Wrench, Component: ServiceObdTab },
  { id: "invoice", label: "Invoice", icon: FileText, Component: InvoiceTab },
  { id: "activity", label: "Activity", icon: Activity, Component: ActivityTab },
];

const STATUS_STYLES = {
  BOOKED: "bg-blue-100 text-blue-800 border-blue-300",
  CONFIRMED: "bg-cyan-100 text-cyan-800 border-cyan-300",
  ASSIGNED: "bg-amber-100 text-amber-800 border-amber-300",
  IN_PROGRESS: "bg-purple-100 text-purple-800 border-purple-300",
  RESCHEDULED: "bg-orange-100 text-orange-800 border-orange-300",
  RESCHEDULED_BY_CUSTOMER: "bg-orange-100 text-orange-800 border-orange-300",
  RESCHEDULED_BY_WD: "bg-orange-100 text-orange-800 border-orange-300",
  COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  CLOSED: "bg-slate-100 text-slate-700 border-slate-300",
  CANCELLED: "bg-rose-100 text-rose-700 border-rose-300",
  CANCELLED_BY_CUSTOMER: "bg-rose-100 text-rose-700 border-rose-300",
  CANCELLED_BY_WD: "bg-rose-100 text-rose-700 border-rose-300",
  MECHANIC_REJECTED: "bg-amber-100 text-amber-800 border-amber-300",
  REFUND_REQUESTED: "bg-pink-100 text-pink-700 border-pink-300",
};
const STATUS_LABELS = {
  BOOKED: "Booked",
  CONFIRMED: "Confirmed",
  ASSIGNED: "Mechanic Assigned",
  IN_PROGRESS: "In Progress",
  RESCHEDULED: "Rescheduled",
  RESCHEDULED_BY_CUSTOMER: "Rescheduled by Customer",
  RESCHEDULED_BY_WD: "Rescheduled by Wisedrive",
  COMPLETED: "Completed",
  CLOSED: "Closed",
  CANCELLED: "Cancelled",
  CANCELLED_BY_CUSTOMER: "Cancelled by Customer",
  CANCELLED_BY_WD: "Cancelled by Wisedrive",
  MECHANIC_REJECTED: "Rejected by Mechanic",
  REFUND_REQUESTED: "Refund Requested",
};

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServiceOpsDetailModal({
  open,
  onClose,
  leadId,
  initialLead = null,
  mechanics = [],
  user = null,
  onChanged,
}) {
  const [lead, setLead] = useState(initialLead);
  const [completion, setCompletion] = useState(initialLead?.completion_pct || 0);
  const [activeTab, setActiveTab] = useState("overview");
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Modal-level draft — sections push edits here via setDraft;
  // footer "Save All Changes" flushes everything in one PATCH.
  const [draft, setDraft] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const catalogue = useStatusCatalogue();

  const isDirty = useMemo(() => Object.keys(draft || {}).length > 0, [draft]);

  // Fetch full lead detail directly from the service-leads detail endpoint
  // (returns the complete document including spare_parts, activities,
  // additional_payment_links, vehicle, service_location etc).
  const fetchLead = useCallback(async () => {
    if (!leadId) return;
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API_URL}/service-leads/${leadId}`);
      if (data) setLead(data);
      const compRes = await serviceOpsApi.completion(leadId);
      setCompletion(compRes.data?.completion_pct || 0);
    } catch (e) {
      console.error("[ServiceOpsDetailModal] fetchLead failed:", e);
    } finally {
      setRefreshing(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) {
      setDraft({});  // reset on open
      fetchLead();
    }
  }, [open, leadId, fetchLead]);

  // Save All Changes — single PATCH from the modal's accumulated draft.
  // Splits fields between three endpoints:
  //   • PATCH /service-leads/{id}    — name/email/city/address/...
  //   • POST  /service-ops/{id}/status — validated against transition table
  //   • POST  /service-ops/{id}/assign-mechanic — fires the WhatsApp notify
  const saveAll = useCallback(async () => {
    if (!isDirty || !lead?.id) return;
    setSavingAll(true);
    console.log("[saveAll] draft:", draft);
    try {
      const draftCopy = { ...draft };

      // Pull out spare_parts (bulk replace endpoint + post-save master upsert)
      const partsToSave = draftCopy.spare_parts;
      delete draftCopy.spare_parts;

      // Pull out status (separate endpoint) and mechanic (separate endpoint)
      const nextStatus = draftCopy.status && draftCopy.status !== lead.status
        ? draftCopy.status : null;
      delete draftCopy.status;

      // Cancellation / refund reason rides along with the status change —
      // never as a field on the lead.
      const reasonCode = draftCopy.reason_code || null;
      const reasonNote = draftCopy.reason_note || null;
      delete draftCopy.reason_code;
      delete draftCopy.reason_note;

      if (nextStatus && requiresReason(catalogue, nextStatus) && !reasonCode) {
        toast.error(`Pick a reason before moving this job to ${nextStatus.replace(/_/g, " ")}`);
        setSavingAll(false);
        return;
      }
      if (nextStatus && reasonCode === "OTHER" && !(reasonNote || "").trim()) {
        toast.error('A note is required when the reason is "Other"');
        setSavingAll(false);
        return;
      }

      const nextMechanic = draftCopy.mechanic_id !== undefined
        && draftCopy.mechanic_id !== lead.assigned_mechanic_id
        ? draftCopy.mechanic_id : null;
      delete draftCopy.mechanic_id;

      // Flatten location coords/landmark into service_location object
      if (draftCopy.coords !== undefined || draftCopy.landmark !== undefined || draftCopy.address !== undefined) {
        const sl = lead.service_location || {};
        draftCopy.service_location = {
          ...sl,
          address: draftCopy.address ?? lead.address ?? sl.address ?? "",
          landmark: draftCopy.landmark ?? sl.landmark ?? null,
          latitude: draftCopy.coords?.lat ?? sl.latitude ?? null,
          longitude: draftCopy.coords?.lng ?? sl.longitude ?? null,
        };
        delete draftCopy.coords;
        delete draftCopy.landmark;
      }

      const fieldKeys = Object.keys(draftCopy);
      let saved = 0;

      // 1) Generic field PATCH
      if (fieldKeys.length > 0) {
        const r = await axios.patch(`${API_URL}/service-leads/${lead.id}`, draftCopy);
        console.log("[saveAll] PATCH ok:", r.status, fieldKeys);
        saved += fieldKeys.length;
      }

      // 2) Mechanic assign (also triggers WhatsApp)
      if (nextMechanic !== null) {
        if (nextMechanic) {
          await serviceOpsApi.assignMechanic(lead.id, { mechanic_id: nextMechanic });
        } else {
          // Unassign: clear the fields via PATCH
          await axios.patch(`${API_URL}/service-leads/${lead.id}`, {
            assigned_mechanic_id: null,
            assigned_mechanic_name: null,
            assigned_mechanic_mobile: null,
          });
        }
        saved += 1;
      }

      // 3) Status transition (validated against backend transition table)
      if (nextStatus) {
        await serviceOpsApi.updateStatus(lead.id, {
          status: nextStatus,
          note: "Changed via Service Ops Overview tab",
          reason_code: reasonCode,
          reason_note: reasonNote,
        });
        saved += 1;
      }

      // 4) Spare parts bulk-replace + per-part master upsert.
      // Master upsert is one-row-per-(name+brand+car_make+car_model). Labor
      // rows are folded into their owning part's master row via labor_for.
      if (Array.isArray(partsToSave)) {
        await serviceOpsApi.replaceParts(lead.id, partsToSave);
        const veh = lead.vehicle || {};
        const partRows = partsToSave.filter((p) => (p.type || "part") !== "labor");
        const laborRows = partsToSave.filter((p) => (p.type || "part") === "labor");
        const laborByPart = {};
        for (const lab of laborRows) {
          if (lab.labor_for) laborByPart[lab.labor_for] = Number(lab.unit_price || 0);
        }
        await Promise.allSettled(
          partRows.map((p) =>
            sparePartsMasterApi.upsertFromOps({
              name: p.name,
              brand: p.brand || null,
              car_make: p.make || veh.brand || null,
              car_model: p.model || veh.model || null,
              hsn_code: p.hsn_code || "8708",
              default_price: Number(p.unit_price || 0),
              labor_amount: laborByPart[p.id] ?? null,
              gst_pct: Number(p.gst_pct ?? 18),
              country_code: lead.country_code || user?.country_code || "IN",
            })
          )
        );
        saved += partsToSave.length;
      }

      toast.success(`Saved ${saved} change${saved === 1 ? "" : "s"}`);
      setDraft({});
      await fetchLead();
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      console.error("[saveAll] failed:", e?.response?.data || e);
      toast.error("Save failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setSavingAll(false);
    }
  }, [isDirty, draft, lead, fetchLead, onChanged, catalogue]);

  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh || !open) return;
    const t = setInterval(fetchLead, 10000);
    return () => clearInterval(t);
  }, [autoRefresh, open, fetchLead]);

  const onLeadChanged = useCallback(
    async (refreshFull = true) => {
      if (refreshFull) await fetchLead();
      if (typeof onChanged === "function") onChanged();
    },
    [fetchLead, onChanged]
  );

  const ActiveComponent = useMemo(
    () => TABS.find((t) => t.id === activeTab)?.Component || OverviewTab,
    [activeTab]
  );

  if (!lead && !initialLead) return null;
  const l = lead || initialLead;

  const customer = l?.customer_name || "Customer";
  const veh = l?.vehicle || {};
  const carStr = [veh.brand, veh.model, veh.rc_number ? `(${veh.rc_number})` : null]
    .filter(Boolean)
    .join(" ");
  const status = l?.status || "BOOKED";
  const slotIso = l?.confirmed_slot || l?.preferred_slot;
  const slotDisplay = slotIso ? `${formatDate(slotIso)} · ${formatTime(slotIso)}` : "—";

  const completionColor =
    completion >= 80 ? "bg-emerald-500" : completion >= 40 ? "bg-amber-500" : "bg-blue-500";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="service-ops-detail-modal"
        className="!w-[1480px] !max-w-[98vw] !h-[94vh] !max-h-[94vh] p-0 gap-0 overflow-hidden bg-white !grid-cols-none !grid-rows-none flex flex-col"
      >
        {/* ── Header strip ────────────────────────────────────────── */}
        <div
          data-testid="ops-modal-header"
          className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex items-start justify-between gap-4 shrink-0"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap mb-2">
              <Car className="h-5 w-5 shrink-0" />
              <h2 className="text-xl font-semibold truncate" data-testid="ops-modal-title">
                {carStr || "Vehicle"} · {customer}
              </h2>
              <Badge
                className={`${STATUS_STYLES[status] || STATUS_STYLES.BOOKED} border font-semibold`}
                data-testid="ops-modal-status-badge"
              >
                {STATUS_LABELS[status] || status.replace(/_/g, " ")}
              </Badge>
              {l?.country_code && l.country_code !== "IN" && (
                <Badge className="bg-orange-100 text-orange-700 border border-orange-300">
                  {l.country_code}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-blue-100">
              <span data-testid="ops-modal-mobile">📞 {l?.mobile || "—"}</span>
              <span>🗓️ {slotDisplay}</span>
              <span>📍 {l?.city || "—"}</span>
              {l?.assigned_mechanic_name && (
                <span>🔧 {l.assigned_mechanic_name}</span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                  data-testid="ops-modal-auto-refresh"
                />
                Auto / 10s
              </label>
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchLead}
                disabled={refreshing}
                className="text-white hover:bg-white/20"
                data-testid="ops-modal-refresh-btn"
                title="Refresh"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="text-white hover:bg-white/20"
                data-testid="ops-modal-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2 min-w-[180px]" data-testid="ops-modal-progress">
              <Progress value={completion} className="h-2 bg-white/30 [&>div]:bg-white" />
              <span className="text-xs font-semibold tabular-nums">{completion}%</span>
            </div>
          </div>
        </div>

        {/* ── Mechanic Rejection Banner ──────────────────────────
            Surfaces when a mechanic taps "Reject" in the mobile app.
            Backend sets:
              status               = 'MECHANIC_REJECTED'
              assigned_mechanic_id = null  (so Ops must reassign)
              rejection            = { reason_code, reason_note,
                                       by_mechanic_id, by_mechanic_name, at }
            The "Reassign Mechanic" CTA jumps to the Overview tab where
            the mechanic dropdown lives. */}
        {l?.status === "MECHANIC_REJECTED" && l?.rejection && (
          <div
            data-testid="mechanic-rejection-banner"
            className="border-b border-amber-300 bg-amber-50 px-6 py-3 flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-amber-900">
                Mechanic rejected this job · waiting to be reassigned
              </div>
              <div className="text-xs text-amber-800 mt-1">
                <span className="font-semibold">
                  {humaniseRejectionCode(l.rejection.reason_code)}
                </span>
                {l.rejection.reason_note ? (
                  <>
                    {" — "}
                    <span className="italic">
                      &ldquo;{l.rejection.reason_note}&rdquo;
                    </span>
                  </>
                ) : null}
              </div>
              <div className="text-[11px] text-amber-700 mt-0.5">
                Rejected by{" "}
                <span className="font-semibold">
                  {l.rejection.by_mechanic_name || "Mechanic"}
                </span>
                {l.rejection.at ? <> · {formatDateTime(l.rejection.at)}</> : null}
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setActiveTab("overview")}
              className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
              data-testid="reassign-mechanic-btn"
            >
              Reassign Mechanic
            </Button>
          </div>
        )}

        {/* ── Body: sidebar + content ────────────────────────────── */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Sidebar */}
          <nav
            data-testid="ops-modal-tabs"
            className="w-48 shrink-0 border-r bg-slate-50 py-2 overflow-y-auto"
          >
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                data-testid={`ops-modal-tab-${id}`}
                onClick={() => setActiveTab(id)}
                className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all border-l-2 ${
                  activeTab === id
                    ? "bg-white text-blue-700 border-blue-600 font-semibold"
                    : "text-slate-600 hover:bg-white hover:text-slate-900 border-transparent"
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div
            className="flex-1 overflow-y-auto bg-white"
            data-testid={`ops-tab-content-${activeTab}`}
          >
            <ActiveComponent
              lead={l}
              mechanics={mechanics}
              user={user}
              onChanged={onLeadChanged}
              completion={completion}
              draft={draft}
              setDraft={setDraft}
            />
          </div>
        </div>

        {/* ── Footer ────────────────────────────────────────────── */}
        <div className="border-t bg-slate-50 px-6 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span>Lead ID: <code className="bg-slate-200 px-1.5 py-0.5 rounded">{l?.id}</code></span>
            <span>·</span>
            <span>
              Updated{" "}
              {l?.updated_at ? formatDateTime(l.updated_at) : "—"}
            </span>
            {isDirty && (
              <span className="bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded text-xs font-semibold" data-testid="dirty-badge">
                {Object.keys(draft).length} unsaved change{Object.keys(draft).length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (isDirty && !confirm("Discard unsaved changes?")) return;
                onClose?.();
              }}
              data-testid="ops-modal-cancel-btn"
            >
              Close
            </Button>
            <Button
              size="sm"
              onClick={saveAll}
              disabled={!isDirty || savingAll}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              data-testid="ops-modal-save-all-btn"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {savingAll ? "Saving..." : "Save All Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
